import { useEffect, useState } from 'react';
import { useQuery } from 'urql';
import { queryDomainLocations } from '../../graphql/location';
import { Location } from '../../location/LocationStep';

interface UseLocationSelectionProps {
  itemsPerPage?: number;
}

interface PageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor: string;
  endCursor: string;
}

export function useLocationSelection({ itemsPerPage = 2 }: UseLocationSelectionProps = {}) {
  // State for locations data
  const [locations, setLocations] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pageInfo, setPageInfo] = useState<PageInfo | null>(null);

  // State for selection
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [selectedLocations, setSelectedLocations] = useState<Location[]>([]);

  // Define type for query variables
  interface QueryVariables {
    filter: {
      isDeleted: {
        is: boolean;
      };
    };
    paging: {
      first?: number;
      after?: string | null;
      last?: number;
      before?: string | null;
    };
  }

  // Query variables state
  const [queryVariables, setQueryVariables] = useState<QueryVariables>({
    filter: {
      isDeleted: {
        is: false
      }
    },
    paging: {
      first: itemsPerPage,
      after: null,
    },
  });

  // GraphQL query
  const [result] = useQuery({
    query: queryDomainLocations,
    variables: queryVariables,
    pause: false,
    requestPolicy: 'network-only',
  });

  const { data, fetching, error } = result;

  // Update state when data changes
  useEffect(() => {
    if (data) {
      setLocations(data.domainLocations.edges);
      setPageInfo(data.domainLocations.pageInfo);
      setIsLoading(false);
    }
  }, [data]);

  // Handle error state
  useEffect(() => {
    if (error) {
      console.error('Error fetching locations:', error);
      setIsLoading(false);
    }
  }, [error]);

  // Update loading state
  useEffect(() => {
    setIsLoading(fetching);
  }, [fetching]);

  // Handle page navigation
  const handlePageChange = (direction: 'next' | 'previous') => {
    if (!pageInfo) return;

    if (direction === 'next' && pageInfo.hasNextPage) {
      setQueryVariables({
        ...queryVariables,
        paging: {
          first: itemsPerPage,
          after: pageInfo.endCursor,
        },
      });
    } else if (direction === 'previous' && pageInfo.hasPreviousPage) {
      setQueryVariables({
        ...queryVariables,
        paging: {
          last: itemsPerPage,
          before: pageInfo.startCursor,
        },
      });
    }
  };

  // Handle single location selection
  const handleLocationSelect = (location: Location) => {
    setSelectedLocation(location);
  };

  // Handle multiple location selection
  const toggleLocationSelection = (location: Location) => {
    setSelectedLocations(prev => {
      const isSelected = prev.some(loc => loc.id === location.id);

      if (isSelected) {
        return prev.filter(loc => loc.id !== location.id);
      } else {
        return [...prev, location];
      }
    });
  };

  // Handle select all
  const selectAllLocations = () => {
    const allLocations = locations.map(edge => edge.node);
    setSelectedLocations(allLocations);
  };

  // Clear all selections
  const clearSelections = () => {
    setSelectedLocation(null);
    setSelectedLocations([]);
  };

  return {
    // State
    locations,
    isLoading,
    pageInfo,
    selectedLocation,
    selectedLocations,

    // Actions
    handlePageChange,
    handleLocationSelect,
    toggleLocationSelection,
    selectAllLocations,
    clearSelections,

    // Helper properties
    hasSelection: selectedLocation !== null || selectedLocations.length > 0,
    error
  };
}
